export const MAX_RECOVERY_FILE_BYTES = 2 * 1024 * 1024;

export function parseTalosUsbQueueExport(payload, expectedDeviceCode = "") {
  const text = String(payload || "").replaceAll("\r\n", "\n");
  if (!text.startsWith("TALOS-OFFLINE/1\n")) throw new Error("This is not a supported Talos USB queue export.");
  const dataStart = text.indexOf("\nDATA\n");
  const dataEnd = text.lastIndexOf("\nEND\n");
  if (dataStart < 0 || dataEnd < dataStart) throw new Error("The USB export ended before the log was complete.");
  const header = text.slice(0, dataStart + 1);
  const deviceMatch = header.match(/^DEVICE:([A-Z0-9-]{6,40})$/m);
  const countMatch = header.match(/^COUNT:(\d+)$/m);
  if (!deviceMatch || !countMatch) throw new Error("The USB export header is incomplete. Try the backup again.");
  const deviceCode = deviceMatch[1];
  const expectedCount = Number(countMatch[1]);
  if (!Number.isSafeInteger(expectedCount) || expectedCount < 0 || expectedCount > 4096) throw new Error("The logger reported an invalid stored-reading count.");
  if (expectedDeviceCode && deviceCode !== expectedDeviceCode) throw new Error(`Connected to ${deviceCode || "another logger"}, not ${expectedDeviceCode}.`);
  const records = text.slice(dataStart + 6, dataEnd).split("\n").filter(Boolean).map((line) => {
    try {
      const record = JSON.parse(line);
      if (!record.recorded_at || !Number.isFinite(Number(record.temperature_c))) throw new Error("invalid record");
      return record;
    } catch {
      throw new Error("The USB export contains a damaged record. Try the backup again.");
    }
  });
  if (records.length !== expectedCount) throw new Error(`Expected ${expectedCount} stored readings but received ${records.length}. Try the backup again.`);
  return { deviceCode, records };
}

function csvValue(value) {
  const text = value == null ? "" : String(value);
  return /[",\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}

export function createTalosOfflineCsv(records) {
  const columns = ["record_id", "recorded_at", "temperature_c", "sensor_rom", "sequence"];
  return [columns.join(","), ...records.map((record) => columns.map((column) => csvValue(record[column])).join(","))].join("\n") + "\n";
}

function parseCsvRows(value) {
  const rows = [];
  let row = [];
  let field = "";
  let quoted = false;
  const text = String(value || "").replace(/^\uFEFF/, "");
  for (let index = 0; index < text.length; index += 1) {
    const character = text[index];
    if (quoted) {
      if (character === '"' && text[index + 1] === '"') { field += '"'; index += 1; }
      else if (character === '"') quoted = false;
      else field += character;
      continue;
    }
    if (character === '"' && field.length === 0) quoted = true;
    else if (character === ",") { row.push(field); field = ""; }
    else if (character === "\n") { row.push(field.replace(/\r$/, "")); rows.push(row); row = []; field = ""; }
    else field += character;
  }
  if (quoted) throw new Error("The recovery CSV contains an unfinished quoted value.");
  if (field.length || row.length) { row.push(field.replace(/\r$/, "")); rows.push(row); }
  while (rows.length && rows[rows.length - 1].every((entry) => entry === "")) rows.pop();
  return rows;
}

export function parseTalosOfflineCsv(payload, expectedDeviceCode) {
  const rows = parseCsvRows(payload);
  const expectedColumns = ["record_id", "recorded_at", "temperature_c", "sensor_rom", "sequence"];
  if (!rows.length || rows[0].length !== expectedColumns.length || rows[0].some((column, index) => column !== expectedColumns[index])) {
    throw new Error("Choose an unmodified Talos offline CSV file.");
  }
  const dataRows = rows.slice(1);
  if (dataRows.length < 1 || dataRows.length > 4096) throw new Error("A recovery CSV must contain between 1 and 4096 readings.");
  const seen = new Set();
  return dataRows.map((columns, index) => {
    if (columns.length !== expectedColumns.length) throw new Error(`CSV row ${index + 2} has the wrong number of columns.`);
    const [recordId, recordedAt, temperatureText, sensorRom, sequenceText] = columns;
    if (!recordId.startsWith(`${expectedDeviceCode}:`)) throw new Error(`CSV row ${index + 2} belongs to another logger.`);
    if (seen.has(recordId)) throw new Error(`CSV row ${index + 2} repeats an earlier record.`);
    seen.add(recordId);
    if (!Number.isFinite(Date.parse(recordedAt))) throw new Error(`CSV row ${index + 2} has an invalid timestamp.`);
    const temperature = Number(temperatureText);
    if (!Number.isFinite(temperature) || temperature < -55 || temperature > 125) throw new Error(`CSV row ${index + 2} has an invalid temperature.`);
    const sequence = sequenceText === "" ? undefined : Number(sequenceText);
    if (sequence !== undefined && (!Number.isInteger(sequence) || sequence < 0)) throw new Error(`CSV row ${index + 2} has an invalid sequence.`);
    if (sensorRom.length > 32) throw new Error(`CSV row ${index + 2} has an invalid sensor identifier.`);
    return { record_id: recordId, recorded_at: recordedAt, temperature_c: temperature, ...(sensorRom ? { sensor_rom: sensorRom } : {}), ...(sequence !== undefined ? { sequence } : {}) };
  });
}
